import * as React from 'react'

import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import UserAvatar from 'modules/core/components/UserAvatar'
import createComputed from 'modules/core/hooks/createComputed'
import createMemo from 'modules/core/hooks/createMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { employeeUser } from 'modules/core/store/collections/users'

import { employeeReferenceContract } from 'modules/contracts/lib/utils'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import { employeeJobLevel } from 'modules/job_catalog/store/collections/levels'
import memberships from 'modules/teams/store/collections/memberships'
import type { ITeam } from 'modules/teams/store/models/Team'

import Settings from './_Settings'

interface Props {
  employee: Employee
  team: ITeam
  readonly?: boolean
  contractVersions: ContractVersions
}

const useGetMembership = createMemo((employeeId, teamId) =>
  memberships.find({
    employee_id: employeeId,
    team_id: teamId,
  })
)

const useMemoHiredOn = createComputed(
  (employee, contractVersions) =>
    employeeReferenceContract(contractVersions, employee)?.startsOn?.fromNow() || '-'
)

const Item = ({ employee, team, readonly, contractVersions }: Props) => {
  const hiredOn = useMemoHiredOn(employee, contractVersions)
  const membership = useGetMembership(employee.id, team.id)
  const { navigation } = useNavigation()

  const assertCellEllipsis = (text: string, maxCharacters = 8): boolean => {
    return text.length > maxCharacters
  }

  const fitText = (text: string) => {
    return (
      <Text.Normal
        weight="medium"
        whiteSpace="nowrap"
        ellipsis={assertCellEllipsis(text || '', 14)}
      >
        {text}
      </Text.Normal>
    )
  }

  const jobLevel = employeeJobLevel(contractVersions, employee)
  const levelName = jobLevel?.isDefault ? '' : jobLevel?.attributes.name
  return (
    <Table.Row>
      <Table.Cell colSpan={2}>
        <Box flexDirection="row" gap="s24" alignItems="center">
          <UserAvatar size="s40" user={employeeUser(employee)} />
          {fitText(
            `${employeeUser(employee).preferredName || ''} ${employeeUser(employee).lastName || ''}`
          )}
        </Box>
      </Table.Cell>
      <Table.Cell>{fitText(levelName || '')}</Table.Cell>

      <Table.Cell>
        <Text.Normal weight="normal">{hiredOn.get()}</Text.Normal>
      </Table.Cell>
      <Table.Cell colSpan={2}>
        {fitText((employee.manager && employeeUser(employee.manager)?.fullName()) || '')}
      </Table.Cell>
      <Table.Cell>
        <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
          {membership && <Settings membership={membership} readonly={readonly} />}
          <Button.Router
            to={navigation.employees.id.fullPath({ id: employee.id })}
            type="secondary"
            iconName="arrow-right"
          />
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default observer(Item)
